import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import TrainerAlertsPanel from '../components/TrainerAlertsPanel';
import {
  BarChart3,
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  Target,
  Calendar,
  Award,
  MessageCircle,
  RefreshCw,
  ChevronDown,
  Flame,
  Scale,
  UsersRound
} from 'lucide-react';

export default function AnalyticsDashboardPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [overview, setOverview] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [progressSummary, setProgressSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';

  const fetchAnalytics = useCallback(async () => {
    if (!isTrainer) return;
    
    setLoading(true);
    try {
      const [overviewRes, clientsRes, trendsRes, progressRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/analytics/overview?days=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/clients?days=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/trends?metric=workouts&days=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/progress-summary?days=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/analytics/leaderboard?metric=engagement&limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setOverview(overviewRes.data);
      setClientData(clientsRes.data);
      setTrends(trendsRes.data);
      setProgressSummary(progressRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [token, period, isTrainer]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (!isTrainer) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">Trainer Access Required</h2>
          <p className="text-gray-400">Analytics dashboard is only available to trainers.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="analytics-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            ANALYTICS <span className="text-primary">DASHBOARD</span>
          </h1>
          <p className="text-gray-400 mt-1">Monitor your studio's performance and client progress</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value))}
            className="bg-secondary text-white rounded-sm px-3 py-2 border-0"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAnalytics}
            className="border-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Alerts Panel */}
      <TrainerAlertsPanel />

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-tactical">
            <div className="flex items-center justify-between">
              <Users className="w-8 h-8 text-blue-500" />
              <span className={`text-xs px-2 py-0.5 rounded ${
                overview.clients.new_signups > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
              }`}>
                +{overview.clients.new_signups} new
              </span>
            </div>
            <div className="mt-3">
              <div className="text-3xl font-mono text-white">{overview.clients.total}</div>
              <div className="text-sm text-gray-400">Total Clients</div>
              <div className="text-xs text-gray-500 mt-1">
                {overview.clients.active} active ({overview.clients.retention_rate}% retention)
              </div>
            </div>
          </div>

          <div className="card-tactical">
            <div className="flex items-center justify-between">
              <Activity className="w-8 h-8 text-green-500" />
              <span className={`text-xs px-2 py-0.5 rounded ${
                overview.engagement.completion_rate >= 70 ? 'bg-green-500/20 text-green-400' :
                overview.engagement.completion_rate >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {overview.engagement.completion_rate}%
              </span>
            </div>
            <div className="mt-3">
              <div className="text-3xl font-mono text-white">{overview.engagement.workouts_completed}</div>
              <div className="text-sm text-gray-400">Workouts Completed</div>
              <div className="text-xs text-gray-500 mt-1">
                {overview.engagement.checkins_logged} check-ins logged
              </div>
            </div>
          </div>

          <div className="card-tactical">
            <div className="flex items-center justify-between">
              <DollarSign className="w-8 h-8 text-yellow-500" />
            </div>
            <div className="mt-3">
              <div className="text-3xl font-mono text-white">${overview.revenue.monthly_recurring}</div>
              <div className="text-sm text-gray-400">Monthly Revenue</div>
              <div className="text-xs text-gray-500 mt-1">
                {overview.revenue.active_subscriptions} active subscriptions
              </div>
            </div>
          </div>

          <div className="card-tactical">
            <div className="flex items-center justify-between">
              <UsersRound className="w-8 h-8 text-purple-500" />
            </div>
            <div className="mt-3">
              <div className="text-3xl font-mono text-white">{overview.teams.total}</div>
              <div className="text-sm text-gray-400">Active Teams</div>
            </div>
          </div>
        </div>
      )}

      {/* Trends & Progress Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Workout Trends */}
        {trends && (
          <div className="card-tactical">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-white uppercase">Workout Trends</h3>
              <div className={`flex items-center gap-1 text-sm ${
                trends.trend_direction === 'up' ? 'text-green-400' :
                trends.trend_direction === 'down' ? 'text-red-400' :
                'text-gray-400'
              }`}>
                {trends.trend_direction === 'up' ? <TrendingUp className="w-4 h-4" /> :
                 trends.trend_direction === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
                {trends.trend_percentage > 0 ? '+' : ''}{trends.trend_percentage}%
              </div>
            </div>
            
            {/* Simple bar chart */}
            <div className="flex items-end gap-1 h-32">
              {trends.data.slice(-14).map((dp, idx) => {
                const maxValue = Math.max(...trends.data.slice(-14).map(d => d.value), 1);
                const height = (dp.value / maxValue) * 100;
                return (
                  <div
                    key={idx}
                    className="flex-1 bg-primary/50 hover:bg-primary transition-colors rounded-t-sm"
                    style={{ height: `${Math.max(height, 2)}%` }}
                    title={`${dp.date}: ${dp.value} workouts`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{trends.data[trends.data.length - 14]?.date.slice(5)}</span>
              <span>Today</span>
            </div>
            <div className="mt-3 text-center">
              <span className="text-2xl font-mono text-white">{trends.total}</span>
              <span className="text-gray-400 ml-2">total workouts</span>
            </div>
          </div>
        )}

        {/* Progress Summary */}
        {progressSummary && (
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4">Client Progress</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                <div className="flex items-center gap-3">
                  <Scale className="w-6 h-6 text-blue-500" />
                  <span className="text-gray-300">Avg Weight Change</span>
                </div>
                <span className={`text-xl font-mono ${
                  progressSummary.avg_weight_change_kg < 0 ? 'text-green-400' :
                  progressSummary.avg_weight_change_kg > 0 ? 'text-yellow-400' :
                  'text-gray-400'
                }`}>
                  {progressSummary.avg_weight_change_kg > 0 ? '+' : ''}
                  {progressSummary.avg_weight_change_kg || 0} kg
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-green-500/10 rounded-sm">
                  <div className="text-2xl font-mono text-green-400">{progressSummary.clients_losing_weight}</div>
                  <div className="text-xs text-gray-500">Losing Weight</div>
                </div>
                <div className="text-center p-3 bg-gray-500/10 rounded-sm">
                  <div className="text-2xl font-mono text-gray-400">{progressSummary.clients_maintaining}</div>
                  <div className="text-xs text-gray-500">Maintaining</div>
                </div>
                <div className="text-center p-3 bg-yellow-500/10 rounded-sm">
                  <div className="text-2xl font-mono text-yellow-400">{progressSummary.clients_gaining_weight}</div>
                  <div className="text-xs text-gray-500">Gaining</div>
                </div>
              </div>

              <div className="text-sm text-gray-500 text-center">
                {progressSummary.clients_tracked} clients tracked • {progressSummary.total_entries} entries logged
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Client Leaderboard & Top Performers */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="card-tactical">
          <h3 className="font-heading text-lg text-white uppercase mb-4">
            <Award className="w-5 h-5 inline mr-2 text-yellow-500" />
            Top Performers
          </h3>
          
          {leaderboard.length === 0 ? (
            <div className="text-center text-gray-500 py-6">
              <Award className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No client data yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((client) => (
                <div
                  key={client.id}
                  className={`flex items-center justify-between p-3 rounded-sm ${
                    client.rank === 1 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                    client.rank === 2 ? 'bg-gray-400/10 border border-gray-400/30' :
                    client.rank === 3 ? 'bg-amber-700/10 border border-amber-700/30' :
                    'bg-secondary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      client.rank === 1 ? 'bg-yellow-500 text-black' :
                      client.rank === 2 ? 'bg-gray-300 text-black' :
                      client.rank === 3 ? 'bg-amber-700 text-white' :
                      'bg-secondary text-gray-400'
                    }`}>
                      {client.rank}
                    </div>
                    <div>
                      <div className="text-white font-medium">{client.name || 'Client'}</div>
                      <div className="text-xs text-gray-500">
                        {client.points} pts • {client.streak} day streak • {client.badges} badges
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-mono text-primary">{client.score}</div>
                    <div className="text-xs text-gray-500">score</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Client Engagement Table */}
        {clientData && (
          <div className="card-tactical">
            <h3 className="font-heading text-lg text-white uppercase mb-4">
              Client Engagement
            </h3>
            
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {clientData.clients.slice(0, 8).map((client, idx) => (
                <div
                  key={client.id}
                  className="flex items-center justify-between p-2 bg-secondary/30 rounded-sm"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm w-6">#{idx + 1}</span>
                    <div>
                      <div className="text-white text-sm">{client.name || 'Client'}</div>
                      <div className="text-xs text-gray-500">
                        {client.workouts_completed} workouts • {client.checkins} check-ins
                      </div>
                    </div>
                  </div>
                  <div className={`px-2 py-0.5 rounded text-xs ${
                    client.completion_rate >= 80 ? 'bg-green-500/20 text-green-400' :
                    client.completion_rate >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {client.completion_rate}%
                  </div>
                </div>
              ))}
            </div>
            
            {clientData.clients.length > 8 && (
              <p className="text-center text-sm text-gray-500 mt-3">
                +{clientData.clients.length - 8} more clients
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
